import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { ImageDto } from 'src/app/models/image-dto';
import { HbFinding, HbSectionDto } from 'src/app/models/hb-section-dto';

@Component({
  selector: 'app-hb-section-details',
  templateUrl: './hb-section-details.component.html',
  styleUrls: ['./hb-section-details.component.scss'],
})
export class HbSectionDetailsComponent {
  @Input() details!: HbSectionDto;
  @Input() findings: HbFinding[] = [];
  @Input() inReportOnly = true;
  @Input() withPhotosOnly = true;
  @Input() selectedEntityId: number | null = null;
  @Input() currentLanguage!: Observable<string>;
  @Output() inReportOnlyChange = new EventEmitter<boolean>();
  @Output() withPhotosOnlyChange = new EventEmitter<boolean>();
  @Output() highlightFinding = new EventEmitter<number>();

  // point / line / polygon, as the סוג column names them.
  private readonly geometryLabels: { [geometryType: string]: string } = {
    point: 'iaa.app.hbSection.geometry.point',
    line: 'iaa.app.hbSection.geometry.line',
    polygon: 'iaa.app.hbSection.geometry.polygon',
  };

  get executionDates(): string | null {
    return [this.details?.fromDate, this.details?.toDate]
      .map((date) => this.toDisplayDate(date))
      .filter((date): date is string => !!date)
      .join(' - ') || null;
  }

  // dd/MM/yyyy without @angular/common's formatDate, which the build left unresolved.
  // The yyyy-MM-dd prefix is read directly so the timezone cannot shift the day.
  private toDisplayDate(value: string | null | undefined): string | null {
    if (!value) return null;
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) {
      return Number(iso[1]) <= 1 ? null : `${iso[3]}/${iso[2]}/${iso[1]}`;
    }
    const d = new Date(value);
    if (isNaN(d.getTime()) || d.getFullYear() <= 1) return null;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  geometryLabel(geometryType: string): string {
    return this.geometryLabels[geometryType] || '';
  }

  // pic_desc rides along as imageDescription so the viewer captions each photo itself.
  images(finding: HbFinding): ImageDto[] {
    return finding.images.map((image) => new ImageDto(
      image.attachmentId, image.mediaUrl, undefined, undefined, image.picDesc ?? undefined));
  }

  // The thumbnail carries no caption; pic_desc is shown only in the enlarged view.
  thumbnails(finding: HbFinding): ImageDto[] {
    return finding.images.map((image) => new ImageDto(
      image.attachmentId,
      image.mediaUrl.includes('?') ? `${image.mediaUrl}&type=thumbnail` : `${image.mediaUrl}?type=thumbnail`));
  }


  onMapIconClick(finding: HbFinding, event: Event): void {
    event.stopPropagation();
    this.highlightFinding.emit(finding.entityId);
  }
}
